import { driver } from 'driver.js';
import { useEffect, useMemo } from 'react';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { useTutorialContext } from '../contexts/TutorialContext';

const areElementsRendered = ({ ids = [], withinId }) => {
  if (ids.length === 0 || !withinId) return true;
  return ids.every(id => document.getElementById(id) !== null);
};

export const usePageTutorial = ({ steps, feature, disable, waitForElements = {}, config = {} }) => {
  const { tutorial, setTutorial, setSeenNewFeature, setEnabled } = useTutorialContext();
  const { currentUser } = useCurrentUser();

  useEffect(() => {
    if (disable) return;
    setEnabled(true);

    return () => {
      setEnabled(false);
    };
  }, [setEnabled, disable]);

  // Auto-trigger tutorial if user hasn't seen this feature yet
  useEffect(() => {
    if (feature && currentUser && !disable) {
      const seenFeatures = currentUser?.features || {};
      const hasSeenFeature = seenFeatures[feature];
      if (!hasSeenFeature) {
        setTutorial(true);
      }
    }
  }, [feature, currentUser, disable, setTutorial]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy useMemo, will refactor later
  const pageTutorialDriver = useMemo(
    () =>
      driver({
        ...config,
        showProgress: true,
        onDestroyed: () => {
          setTutorial(false);
          feature && setSeenNewFeature(feature);
          config.onDestroyed?.();
        },
        steps,
      }),
    [steps, config, setTutorial, feature, setSeenNewFeature],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy useEffect, will refactor later
  useEffect(() => {
    if (!disable && tutorial) {
      if (areElementsRendered(waitForElements)) {
        pageTutorialDriver.drive();
      } else {
        // If withinId is not set, start the tutorial immediately
        if (!waitForElements.withinId) {
          pageTutorialDriver?.drive?.();
          return;
        }

        const containerElement = document.getElementById(waitForElements.withinId);

        // If the container doesn't exist yet, wait for it
        if (!containerElement) {
          return;
        }

        const rendered = waitForElements.ids.map(() => false);
        let hasStarted = false;

        const checkAndStart = () => {
          if (!hasStarted && rendered.every(v => v)) {
            hasStarted = true;
            observer.disconnect();
            pageTutorialDriver?.drive?.();
          }
        };

        const observer = new MutationObserver(mutations => {
          mutations.forEach(m => {
            if (m.addedNodes.length > 0) {
              m.addedNodes.forEach(node => {
                // Check if the node itself has the ID
                if (node.id) {
                  const index = waitForElements.ids.indexOf(node.id);
                  if (index !== -1) {
                    rendered[index] = true;
                  }
                }

                // Also check descendants in case elements are nested
                if (node.querySelectorAll) {
                  waitForElements.ids.forEach((id, index) => {
                    if (node.querySelector?.(`#${id}`)) {
                      rendered[index] = true;
                    }
                  });
                }
              });
            } else if (m.nextSibling?.id) {
              const index = waitForElements.ids.indexOf(m.nextSibling.id);
              if (index !== -1) {
                rendered[index] = true;
              }
            }
          });
          checkAndStart();
        });

        observer.observe(containerElement, {
          childList: true,
          subtree: true,
          attributes: false,
          characterData: false,
        });

        // Cleanup function
        return () => {
          observer.disconnect();
        };
      }
    }
  }, [disable, tutorial, pageTutorialDriver, waitForElements]);
};
